/* The Offer Ladder: her offers, grouped by what each one is FOR.
 *
 * The oldest panel in Momentum and the one the others copied — the ADD → save →
 * edit store pattern started here. On the screen pattern since 2026-09-15
 * (`3036ee3`); where an offer sends people is `DestinationField`, at the bottom.
 */
import { useState } from "react";
import { buildMomentumContext } from "../../utils/momentumContext";
import { applyOfferBlock, parseOfferBlock, stripOfferBlock } from "../../utils/offerBlock";
import { PRODUCT_TYPES, SOURCE_SECTION, formatPricing, productTypeOf, readPricing, sourceChanged } from "../../utils/offerParse";
import { ctaLabel, ctaStatus } from "../../utils/useCtas";
import { destinationOf, pointAt, pointNowhere } from "../../utils/offerDestination";
import { B, STATUS_LABELS, btnGhost, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, AssistButton, Badge, Bread, ChatPane, Choice, ContextChips, Field, Hint, Menu2, ModeToggle, Note, RefreshTag, SourceTag, Status, floatingPanel, useAnchoredPanel, useTwoPane } from "./ui";
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { PushToCrm } from "./pushToCrm";
import { useSession } from "@clerk/clerk-react";
import { CHAT_HEIGHT_DRAFTING, CHAT_HEIGHT_PHONE } from "../../constants/layout";
import { offerLine } from "../../utils/offerLine";
import { useVoiceRecorder } from "../../utils/voiceDrafts";
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Home, Link2, Plus, Send, Trash2 } from "lucide-react";

/* "Core" is the ladder *position* — the flagship rung. "Your Signature Offer" is
   the Accelerator section that names one specific offer filling that position.
   Keeping the type generic is what lets Core be multi-instance: a 1:1 version and
   a group version are two Core offers, and a type literally called "Signature
   Offer" stops making sense the moment a second one exists.

   The key must stay `core` regardless of the label — renaming it would make every
   offer already saved under `core` fall through to Other. */
export const OFFER_TYPES = {
  trust: { label: "Trust Building", badge: "trust", ico: "🎁", hint: "A low-barrier lead-in — masterclass, quiz / lead magnet, or other." },
  core: { label: "Core", badge: "core", ico: "⭐", hint: "Your flagship offer — the main transformation." },
  continuation: { label: "Continuation", badge: "cont", ico: "🔁", hint: "A recurring tier after the main transformation." },
  other: { label: "Other", badge: "trust", ico: "✳️", hint: "Something else — describe it in your own words." },
};


/* A ladder has rungs in an order, so the list follows the ladder rather than the
   order things were added. Retired offers are pulled out into their own group at
   the end — see `groupOffers`. */
export const TYPE_ORDER = ["trust", "core", "continuation", "other"];


/**
 * HER ORDER INSIDE A RUNG, newest first until she has dragged.
 *
 * `sort_order` has been a column since 0001 with nothing writing it, so every
 * row she already has holds 0 — which is a tie, and a tie means Postgres
 * returns them in whatever order it likes. Sorting on it alone would have
 * scrambled the ladder she is looking at today. So the tie falls back to
 * `created_at` DESCENDING, which IS the order the panel has always shown, and
 * her first drag writes real numbers over the lot (see `offerReorderWrites`).
 */
function inHerOrder(rows) {
  return [...rows].sort((a, b) => {
    const d = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (d) return d;
    return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
  });
}

export function groupOffers(offers) {
  const live = offers.filter((o) => o.status !== "retired");
  const retired = offers.filter((o) => o.status === "retired");
  const groups = TYPE_ORDER
    .map((type) => ({ key: type, label: OFFER_TYPES[type].label, rows: inHerOrder(live.filter((o) => (o.type || "other") === type)) }))
    .filter((g) => g.rows.length);
  if (retired.length) groups.push({ key: "retired", label: "Retired", rows: inHerOrder(retired), dim: true });
  return groups;
}

/**
 * Reordering inside ONE rung, renumbering the whole ladder afterwards.
 *
 * The table and the rail are banded by rung, so both report a move as an index
 * WITHIN its band. A DROP ONTO ANOTHER RUNG IS IGNORED rather than changing the
 * offer's type: dragging her Core offer under "Trust Building" would silently
 * re-rung it, and the rung is the one thing about an offer that everything else
 * reads — the badge on a page, the calls that serve it, the scripts filtered by
 * it. Changing a rung is a decision, and it lives behind the type field.
 *
 * Renumbered rather than swapped (rule 10), so the ties every existing row
 * carries repair themselves the first time she drags anything.
 */
export function offerReorderWrites(offers = [], bandKey, from, to) {
  const groups = groupOffers(offers);
  const band = groups.find((g) => g.key === bandKey);
  if (!band || from === to || from < 0 || to < 0 || from >= band.rows.length || to >= band.rows.length) return [];
  const rows = [...band.rows];
  rows.splice(to, 0, rows.splice(from, 1)[0]);
  const flat = groups.flatMap((g) => (g.key === bandKey ? rows : g.rows));
  return flat.map((o, i) => ({ ...o, sortOrder: i })).filter((o, i) => (flat[i].sortOrder ?? null) !== i);
}


/* THE FIRST TRACK IS THE GRIP. L1Table puts a drag handle in front of every
   row once a table is reorderable, so a cols string without a column for it has
   one track too few: the bin wraps onto a second line under the grip and every
   cell after it slides one column right. Shipped exactly like that for an hour
   on 2026-09-17 — the same 28px every other draggable table starts with. */
/* NO PRICE IN THE TABLE (her call, 2026-10-15: "I can't show it because of
   the price and I keep landing on that screen"). She demos the app, and the
   table is where the Offer Ladder opens. The price stays inside each offer. */
export const LADDER_COLS = "28px 2fr 1.15fr .95fr 1.1fr 36px";


/* What survives the panel's old paragraph, on the same rule as Sites and
   Courses (CLAUDE.md 9e): only what she cannot deduce by looking at the table.
   The rungs and the pricing are on screen; what a ladder IS, and the fact that
   editing here never touches the Accelerator record, are not. */
const OFFER_HINT = <>Every offer you sell, low-barrier lead-in to flagship to
  recurring. Your signature offer comes across from the Accelerator and is a
  COPY — edit it freely here, the locked record is not touched. Re-lock it there
  and the row will say it may need a refresh.</>;


export function OfferLadder({
  offers, ctas = [], pages = [], sites = [], onAdd, onOpen, onDelete, menuOpen, setMenuOpen, offerOutputs,
  canPull, onPull, loading, error, onRetry, onDismissError, view, setView, onReorder,
}) {
  return (
    <>
      <Bread text="Build" />
      <h2 style={pTitle}>Offer Ladder</h2>
      <p style={pDesc}>{OFFER_HINT}</p>

      {/* The bar every panel shares. Offers have no Level 1 — like Outreach —
          so the Table / Offer toggle stays a genuine toggle: both views show the
          same set of offers, one as a list and one as the thing she is writing
          (CLAUDE.md 9b). */}
      <MomentumBar view={view} setView={setView} copyLabel="Offer" add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add offer" onClick={() => setMenuOpen((o) => !o)} />
          {menuOpen && (
            <Menu2 head="What kind of offer?" items={[
              /* Conditionally present rather than disabled — Menu2 reads `disabled`
                 as "coming soon", which this isn't. */
              ...(canPull ? [["⬇️", "Pull from Signature Offer", "Bring your locked Signature Offer across from the Accelerator.", false, onPull]] : []),
              ["🎁", "Trust Building", "Low-barrier lead-in — masterclass, quiz / lead magnet, or other.", false, () => onAdd("trust")],
              ["⭐", "Core", "Your flagship offer — the main transformation. You can have more than one.", false, () => onAdd("core")],
              ["🔁", "Continuation", "Recurring tier after the main transformation.", false, () => onAdd("continuation")],
              ["✳️", "Other", "Something else — describe it.", false, () => onAdd("other")],
            ]} />
          )}
        </div>
      } />

      {/* A write that failed saved NOTHING, so this banner is the only thing
          standing between her and believing it worked. Retry re-reads from
          Supabase; Dismiss just hides the message. */}
      {error && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: B.salmonQuiet, border: `1px solid ${B.salmonLine}`, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ flex: 1, fontSize: 12.5, color: B.salmon, lineHeight: 1.55 }}>
            <b>Your offers didn't save.</b> {error} Nothing was lost — try again once you're back online.
          </div>
          <button onClick={onRetry} style={{ ...btnGhost, borderColor: B.salmonLine, color: B.salmon, flexShrink: 0 }}>Retry</button>
          <button onClick={onDismissError} style={{ ...btnGhost, flexShrink: 0 }}>Dismiss</button>
        </div>
      )}

      {/* Grouped by rung rather than by when it was added — a ladder has an
          order and the table should read in it. `groups` is L1Table's optional
          shape; every other panel hands it a flat list.

          IT HAS A GRIP NOW (her ask, 2026-09-17: "offer items should be
          draggable to reorder"). It did not, because `sort_order` round-tripped
          with nothing writing it and a grip that puts a row back where it was
          reads as broken rather than absent. `offerReorderWrites` writes it,
          inside one rung — L1Table hands the band key over with the indices. */}
      <L1Table
        groups={groupOffers(offers)} cols={LADDER_COLS}
        labels={["Offer", "Type", "Status", "Sends to", ""]}
        loading={loading} loadingText="Loading your offers…"
        onOpen={onOpen} onDelete={onDelete} deleteTitle="Delete offer"
        onReorder={onReorder ? (from, to, band) => onReorder(band, from, to) : undefined}
        empty={canPull ? (
          /* Don't tell her it arrives on its own when it plainly hasn't. If
             there's something to pull and the table is empty, the honest thing
             is to hand her the button. */
          <>
            <div>Your <b style={nb}>Signature Offer</b> is locked in My Offer and ready to bring across.</div>
            <button onClick={onPull} style={{ ...btnGhost, marginTop: 14, borderColor: B.tealLine, color: B.teal }}>Pull from Signature Offer</button>
            <div style={{ marginTop: 12, fontSize: 11.5, color: B.faint }}>Or click <b style={nb}>Add offer</b> to build a rung by hand.</div>
          </>
        ) : (
          <>No offers yet. Once your <b style={nb}>Signature Offer</b> is locked in My Offer it lands here on its own. You can also click <b style={nb}>Add offer</b> to build a rung by hand.</>
        )}
        /* ON A PHONE: her name for it, where it stands, and a way in. Nothing
           else — the price came off with the table's column (2026-10-15). The type pill is gone because the rung heading
           directly above the row already says Trust Building, and the summary
           is gone because it only ever showed its first four words. An empty
           price says so in words rather than as a dash on a line of its own. */
        narrowRowFor={(o) => (
          <>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>{o.name || "Untitled offer"}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Status cls={o.status} dotOnly />
              <ChevronRight size={16} color={B.faint} />
            </div>
          </>
        )}
        rowFor={(o) => {
          const t = OFFER_TYPES[o.type] || OFFER_TYPES.other;
          const stale = sourceChanged(o, offerOutputs);
          /* Tolerant lookup, through the one reader: a page or link deleted in
             another tab leaves its id on our copy of the row, and that reads as
             "deleted" in amber rather than as a name that isn't there. */
          const dest = destinationOf(o, { pages, sites, ctas });
          return (
            <>
              <div style={{ minWidth: 0 }}>
                {/* The row's anchor, at the weight Sites and Courses use — it is
                    what she scans, and it was the same size as the pricing three
                    columns over. */}
                <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", lineHeight: 1.3 }}>
                  <span>{o.name || "Untitled offer"}</span>
                  {o.source === "accelerator" && <SourceTag />}
                  {stale && <RefreshTag />}
                </div>
                {o.summary && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.summary}</div>}
              </div>
              <div><Badge cls={t.badge} text={t.label} /></div>
              <div><Status cls={o.status} /></div>
              {/* Where the offer sends people. The "tied to" line that sat under
                  it went with the field (2026-09-16) — "Supported by" on the
                  offer answers that now, from records rather than typing. */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: !dest ? B.faint : dest.broken ? B.amber : B.mid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {dest ? dest.label : "—"}
                </div>
              </div>
            </>
          );
        }} />

      {/* THE PHONE ROW HAS NO GRIP, so on a phone this used to be instructions
          for a control that is not on the screen — her report, 2026-09-18,
          from the same pass that found the double back. Mine: the grip came
          off in 7c10ed2 and the sentence describing it did not.
          What survives is the half that is still true, plus where the missing
          half lives, because "you cannot reorder here" is worth more than
          silence when the rows are plainly in an order. */}
      {/* The grip is back on the phone row (2026-09-23), so one sentence again. */}
      {offers.length > 1 && (
        <Note><b style={nb}>Drag by the grip to put a rung in your order.</b> Dragging
          stays inside one rung — moving an offer to a different rung is the Type
          field, in the offer itself.</Note>
      )}

      {/* THE "SAVED IN THIS BROWSER" NOTE IS GONE, and it was not a tidy-up:
          it was false. Offers moved to Supabase when `useOffers.js` replaced
          `offerStore.js` — which survives only as the migration source — and
          this panel went on telling her for weeks that her work lived in her
          browser and that "Supabase sync is next". If she had believed it she
          would have avoided switching machines to protect work that was never
          at risk. What is worth keeping out of it is in OFFER_HINT above. */}
    </>
  );
}


/* The rail of offers beside the one she is writing — CLAUDE.md 9c, and the last
   panel to get it.
 *
 * "It's worth noting that Offer Ladder doesn't follow the rest of the UI — it
 * doesn't have the list of offers on the left when you drill in… is that OK,
 * it's a form so maybe?" The FORM is fine and stays a form: `RecordCopy` is
 * handed children and does not care whether they are a stack of blocks or a set
 * of fields. What was not fine is the missing rail, and the argument for it is
 * stronger here than anywhere else — a ladder is comparative. She prices the
 * Core rung against the Trust Building one, and doing that meant backing out to
 * the table and drilling in again.
 *
 * Ordered by RUNG, not by when they were added, so the rail reads in the same
 * order as the table it came from — and draggable inside a rung, the same as
 * that table (2026-09-17).
 */
export function OfferCopy({
  offers = [], selectedId, onSelect, drafting, view, setView, onBack, offer, onReorder, children,
}) {
  const groups = groupOffers(offers);
  const ordered = groups.flatMap((g) => g.rows);
  const t = OFFER_TYPES[offer?.type] || OFFER_TYPES.other;
  /* On a phone RecordCopy renders the way back (levels={2} + onExit), so the
     bar stands down — exactly as Sites and Courses do. Missing this here is
     how "← All offers" shipped TWICE, stacked, in b8389d1: the fix went
     through the two three-level panels and not the flat one, which has its own
     `lead` in this file. Found by her on a phone within the hour. */
  const twoPane = useTwoPane();
  /* BRAINSTORMING IS THE DRAFTING SCREEN (2026-09-24, her call: "put the offer
     brainstorm on it too"). The crumb, the heading, the bar and the rail stand
     down, and OfferForm draws the one row — the way back and which offer. */
  if (drafting) return children;
  return (
    <>
      {/* The same shape as Page copy: crumb, the RECORD's name big, then the one
          shared bar. The panel's name has nowhere else to go once the heading
          belongs to the offer (9d), and the way back lives in the bar's `lead`
          slot like every other panel's (9c) — not only in the footer, which on a
          long form is under everything. */}
      <Bread text="Build" crumb="Offer Ladder" hint={OFFER_HINT} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <h2 style={{ ...pTitle, margin: 0 }}>{offer?.name || `New ${t.label.toLowerCase()} offer`}</h2>
        <Badge cls={t.badge} text={t.label} />
      </div>
      <MomentumBar view={view} setView={setView} copyLabel="Offer" levels={2}
        lead={twoPane && <BarBack label="← All offers" onClick={onBack} />} />
    <RecordCopy
      /* TWO LEVELS: the Table is this list, so a phone skips the list screen
         and comes straight back here. See `levels` in shell.jsx. */
      levels={2} onExit={() => setView("table")} exitLabel="All offers"
      records={ordered} selectedId={selectedId} onSelect={onSelect}
      /* The rail is one flat list in ladder order; a drag is turned back into a
         move inside the dragged offer's own rung, and a drop onto another rung
         is ignored — see `offerReorderWrites`. Same shape as Scripts. */
      onReorder={onReorder ? (from, to) => {
        const a = ordered[from];
        const b = ordered[to];
        if (!a || !b) return;
        const bandOf = (o) => groups.find((g) => g.rows.includes(o));
        if (bandOf(a)?.key !== bandOf(b)?.key) return;
        const band = bandOf(a);
        onReorder(band.key, band.rows.indexOf(a), band.rows.indexOf(b));
      } : undefined}
      noun="offer" bare={drafting}
      emptyHint={<>No offers yet. Switch to <b style={nb}>Table</b> and add one.</>}
      rowFor={(o) => ({
        title: o.name,
        meta: formatPricing(o.pricing) || (OFFER_TYPES[o.type] || OFFER_TYPES.other).label,
        /* Offers answer "where is this up to" with draft / locked / retired,
           which is NOT the draft / done every other panel now uses — and
           `Locked` is a reserved word (Accelerator content only). Flagged, not
           silently renamed: the values are stored, so changing them is a data
           decision and hers. Until then the dot reads what is actually there. */
        dot: o.status === "locked" ? B.teal : o.status === "retired" ? B.ghost : B.amberFill,
        dotTitle: STATUS_LABELS[o.status] || "Draft",
      })}>
      {children}
    </RecordCopy>
    </>
  );
}


export function OfferForm({
  draft, setDraft, onSave, onDelete, onResync, onBack, saving, isMobile, offerOutputs, offers = [], ctas = [], chatContext = {},
  pages = [], sites = [], pageState, onMakeLandingPage, onNewMeetingLink, error, onDismissError,
  supporters = [], onOpenSupporter,
  /* Form / Brainstorm, held by the hub when it is handed in so OfferCopy can
     stand its chrome down while she brainstorms. Local when it is not. */
  mode: modeProp, setMode: setModeProp,
}) {
  const { session } = useSession();
  const fromAccelerator = draft?.source === "accelerator";
  /* Roles are the API's own, not display names — this history is sent as the
     conversation, so a translation step in between is one more thing to get wrong. */
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: fromAccelerator
      ? "This is pulled straight from your locked Signature Offer — structure, price, promise, all of it. Want to tighten the language, adjust the price, or change something about how it's delivered? Tell me what's not sitting right and I'll work from there."
      : "Tell me what you're thinking for this one — or say \"suggest something\" and I'll work from what you've already locked." },
  ]);
  const [chatText, setChatText] = useState("");
  const [sending, setSending] = useState(false);
  const [showSource, setShowSource] = useState(false);
  /* Form and Brainstorm are separate modes rather than side-by-side panes. It makes
     "nothing here writes itself" visible: the form is hers, chat is somewhere else,
     and ideas only cross over when she sends them. This is the shape the rest of
     Momentum moves to. */
  const [localMode, setLocalMode] = useState("form");
  const mode = modeProp || localMode;
  const setMode = setModeProp || setLocalMode;
  /* The drafting screen's line (drafter.jsx): below it the way back is an
     arrow and the chat takes the phone's height. Above the return, like every
     hook (src/hookOrder.test.js). */
  const phone = !useTwoPane(1180);
  const recordVoice = useVoiceRecorder();
  if (!draft) return null;
  const t = OFFER_TYPES[draft.type] || OFFER_TYPES.other;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const price = readPricing(draft.pricing);
  const setPrice = (k, v) => setDraft((d) => ({ ...d, pricing: { ...readPricing(d.pricing), [k]: v } }));
  const canSave = draft.name && draft.name.trim();

  /* ── NOTHING TO PRESS (rule 1) ────────────────────────────────────────────
     This screen had Save offer / Cancel and a caption reading "Nothing saves
     until you save it" — true when it was written, and the last place in
     Momentum where it was. Everything else has saved on the way out of a field
     since August, which is what made this one feel like a different product.

     TWO KINDS OF FIELD, two moments to write.
       `commit`  — typing. Writes on the way OUT, because a save per keystroke is
                   a Supabase round trip per character.
       `setNow`  — a choice: a Free toggle, a status pill, a select. There is no
                   "way out" of a button, and waiting for her to click elsewhere
                   before storing a decision she has already made is how a thing
                   she pressed goes missing.

     `setNow` passes the PATCH to onSave rather than relying on the draft, for
     the reason `savePage` learned the hard way: setDraft is not synchronous, so
     saving straight after setting writes the value from before the click. */
  const commit = () => { if (canSave) onSave(); };
  const setNow = (k, v) => { set(k, v); if (canSave) onSave({ [k]: v }); };
  const setPriceNow = (k, v) => {
    const pricing = { ...readPricing(draft.pricing), [k]: v };
    setPrice(k, v);
    if (canSave) onSave({ pricing });
  };
  const stale = sourceChanged(draft, offerOutputs);
  /* Resolved rather than trusted: if the page or link was deleted while this
     form was open, the field says so in amber, and `saveOffer` drops the dead
     id before it reaches the foreign key. */
  const destination = destinationOf(draft, { pages, sites, ctas });
  /* A choice in the picker is a patch of all three columns, set at once — the
     same `setNow` reasoning, three keys instead of one. */
  const pointOffer = (patch) => { setDraft((d) => ({ ...d, ...patch })); if (canSave) onSave(patch); };
  /* Tells the guide which of its three flows to run. Sent as its own leading turn
     rather than folded into her message, so the guide reads it as instruction and
     she never sees it. Core Path B needs the names of what she already has. */
  const offerContextLine = () => {
    const siblings = (offers || [])
      .filter((o) => o.id !== draft.id && o.type === draft.type && o.name)
      .map((o) => o.name);
    /* THE OFFER AS IT STANDS, "Tell Brill more" included (her ask, 2026-09-24,
       the day that box was added). Every other panel read it through offerLine
       and the one screen where she WRITES the offer did not. Rebuilt per
       message, so it is her words as they are on the form now.
       COST: the box's length on every brainstorm message. */
    const written = [draft.name, draft.summary, draft.details].some((v) => String(v || "").trim());
    return [
      "[OFFER_CONTEXT — read only, do not reference as a conversation]",
      `Offer type: ${draft.type}`,
      ...(written ? [`What she has written for it so far: ${offerLine(draft)}`] : []),
      `Pulled from her locked Signature Offer: ${fromAccelerator ? "yes" : "no"}`,
      siblings.length
        ? `She already has these ${t.label} offers: ${siblings.join("; ")}.`
        : `This is her first ${t.label} offer.`,
    ].join("\n");
  };

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    const next = [...msgs, { role: "user", content: msg }];
    setMsgs(next);
    setChatText("");
    setSending(true);
    try {
      const token = await session?.getToken();
      /* Her locked Accelerator work rides along on every message, scoped by
         CONTEXT_SCOPES to the sections this guide actually names — everything she
         has locked would be far more text than this needs. */
      const ctx = buildMomentumContext({
        moduleId: "offer_ladder",
        acceleratorOutputs: chatContext.acceleratorOutputs,
        activeNiche: chatContext.activeNiche,
        isStale: (id, name) => {
          const stamp = chatContext.outputStamps?.[id]?.[name];
          return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
        },
      });
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          moduleId: "offer_ladder",
          max_tokens: 2400,   // model is chosen server-side; 2400 covers Sonnet 5's ~30% larger token counts
          moduleLabel: "Offer Ladder",
          archetype: chatContext.userSettings?.archetype || null,
          archetypePair: chatContext.userSettings?.archetypePair || null,
          messages: [
            ...(ctx ? [{ role: "user", content: ctx }] : []),
            { role: "user", content: offerContextLine() },
            ...next,
          ],
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 429 and 403 already carry a written explanation; anything else gets one.
        setMsgs((m) => [...m, { role: "assistant", content: data?.error || "Something went wrong reaching Brill — try again in a moment. Your offer is safe; you can always type the fields in on the right." }]);
        return;
      }
      const reply = data.content?.find((b) => b.type === "text")?.text || "";
      /* The lock is a structured block, so nothing here is inferred from her prose
         — see utils/offerBlock.js. Her own edits win over a field Brill omitted. */
      const block = parseOfferBlock(reply);
      if (block) {
        /* Recording the voice (supabase/0030): the fields Brill filled, and what
           each held before. Only the fields it wrote — the rest of the offer is
           not Brill's draft. */
        const filled = applyOfferBlock(draft, block);
        const keys = Object.keys(filled).filter((k) => JSON.stringify(filled[k]) !== JSON.stringify(draft[k]));
        recordVoice({
          tool: "offer_ladder", recordType: "offer", recordId: draft.id, how: "brainstorm",
          draft: Object.fromEntries(keys.map((k) => [k, filled[k]])),
          replaced: Object.fromEntries(keys.map((k) => [k, draft[k]])),
        });
        setDraft((d) => applyOfferBlock(d, block));
      }
      setMsgs((m) => [...m, {
        role: "assistant",
        content: stripOfferBlock(reply) || (block ? "Locked in — I've filled the card on the right. Have a look and save when it reads right." : "Try that again for me?"),
      }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "I couldn't reach Brill just then — check your connection and try again. Nothing you've typed is lost." }]);
    } finally {
      setSending(false);
    }
  };
  /* The per-field assists hand off into Brainstorm with the question already asked,
     rather than writing into the field. Phase 2 replaces this with an inline
     two-variant panel; only this handler changes, not the field layout. */
  const askBrill = (question) => { setMode("brainstorm"); send(question); };

  const words = (draft.summary || "").trim().split(/\s+/).filter(Boolean).length;
  const isFree = /^\s*free\s*$/i.test(price.amount);
  /* Free and the mode chips write BOTH keys at once, so they build the whole
     pricing object rather than going through `setPriceNow` twice — two writes
     for one press, and the second would race the first's state. */
  const savePricing = (patch) => {
    const pricing = { ...readPricing(draft.pricing), ...patch };
    setDraft((d) => ({ ...d, pricing: { ...readPricing(d.pricing), ...patch } }));
    if (canSave) onSave({ pricing });
  };
  const toggleFree = () => savePricing({ mode: "flat", amount: isFree ? "" : "Free" });
  /* NOT `setMode` — that is already the Form / Brainstorm toggle's setter four
     lines up the component, and shadowing it here would have silently stopped
     the mode toggle working while everything still compiled. */
  const setPriceMode = (mode) => savePricing(isFree ? { mode, amount: "" } : { mode });

  // Every chip reflects real content, so the row can be trusted. See ContextChips.
  const has = (mod) => Object.keys(chatContext.acceleratorOutputs?.[mod] || {}).length > 0;
  const contextItems = [
    { label: "My Offer (locked)", on: has("offer") },
    { label: "Audience & archetype", on: has("client") || !!chatContext.userSettings?.archetype },
    { label: "Your voice", on: has("voice") },
    { label: "Existing ladder tiers", on: (offers || []).some((o) => o.id !== draft.id) },
  ];

  return (
    <>
      {/* WHICH OFFER, then what kind it is — the rail on the left says which
          offers exist, so this says which ONE she is in, the way a site's name
          and a module's do (CLAUDE.md 9d). The old drill-in chrome is gone: a
          Back link, a second status pill and a paragraph describing the mode she
          can see the toggle for.

          The name is the heading and it is TYPED here, the way a page's is —
          rule 7. It was a form field in the card below, which is the shape every
          other panel stopped using. */}
      {/* The crumb, the offer's name and the way back are the shell's now
          (`OfferCopy`) — this is the body. What stays is the one control that is
          about this screen rather than about which offer she is in. */}
      {mode === "form" ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <ModeToggle mode={mode} setMode={setMode} />
        </div>
      ) : (
        /* THE DRAFTING SCREEN'S ONE ROW (drafter.jsx): the way back, then which
           offer. No Form / Brainstorm toggle here — the back IS the way to the
           form, and two controls for one move is two things to read. On a phone
           the back shrinks to an arrow, as it does on every drafting screen. */
        <div style={{ display: "flex", alignItems: "center", gap: phone ? 6 : 10, marginBottom: phone ? 10 : 12, minWidth: 0 }}>
          {phone ? (
            <button onClick={() => setMode("form")} aria-label="Back to the offer" title="Back to the offer"
              style={{ ...btnGhost, padding: 8, lineHeight: 0, flexShrink: 0 }}>
              <ArrowLeft size={16} />
            </button>
          ) : (
            <button onClick={() => setMode("form")} style={{ ...btnGhost, padding: "8px 14px", fontSize: 12.5, flexShrink: 0 }}>← Back to the offer</button>
          )}
          <span style={{ fontSize: phone ? 12 : 13, fontWeight: 600, color: B.mid, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {draft.name || `New ${t.label.toLowerCase()} offer`}
          </span>
        </div>
      )}
      {/* Flagged, never auto-revised — the same rule the Accelerator uses when a
          niche changes. She decides whether to take the new version. */}
      {stale && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", border: `1px solid ${B.amberLine}`, background: B.amberQuiet, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: B.mid, lineHeight: 1.55 }}>
            <b style={{ ...nb, color: B.amber }}>My Offer has changed since this was pulled.</b> Nothing here has been touched. You can bring the new version across, or leave this as it is.
          </div>
          <button style={{ ...btnGhost, padding: "8px 14px", fontSize: 12.5, borderColor: B.amberLine, color: B.amber }} onClick={() => onResync(draft)}>Pull the new version</button>
        </div>
      )}
      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: onDismissError ? "pointer" : "default", lineHeight: 1.55 }}>
          <b>This offer didn't save.</b> {error} Your words are still on screen.
        </div>
      )}
      {mode === "form" ? (
      <>
      <div style={{ border: `1px solid ${B.border}`, borderRadius: 14, background: B.surface, padding: isMobile ? "20px 18px 4px" : "28px 30px 10px" }}>
        <Field
          label={<>Offer name <span style={{ color: B.ghost, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(what your audience sees)</span></>}
          action={<AssistButton label="Need ideas?" onClick={() => askBrill(`I need ideas for what to call this ${t.label.toLowerCase()} offer. Give me a few options I can pick from.`)} />}
        >
          <input value={draft.name} onChange={(e) => set("name", e.target.value)} onBlur={commit} placeholder="e.g. The Aligned Business Accelerator" style={input} />
          {fromAccelerator && <Hint>Pulled from your locked {SOURCE_SECTION}. Edit it freely — the Accelerator record isn't changed.</Hint>}
        </Field>
        <div style={{ borderTop: `1px solid ${B.border}`, marginBottom: 18 }} />
        <Field
          label="Description"
          action={<AssistButton label="Tighten this language" disabled={!words}
            onClick={() => askBrill(`Tighten this offer description for me — keep my meaning, make it land harder:\n\n"${draft.summary}"`)} />}
        >
          <textarea value={draft.summary} onChange={(e) => set("summary", e.target.value)} onBlur={commit} placeholder="What this offer is and who it's for, in a sentence or two." style={{ ...input, minHeight: 110, resize: "vertical", lineHeight: 1.5 }} />
          {/* Guidance, not a limit — nothing blocks a longer description. */}
          <Hint>{words === 0 ? "Aim for 25 words or fewer." : `${words} word${words === 1 ? "" : "s"} — aim for 25 or fewer.`}</Hint>
        </Field>
        {/* THE LONG VERSION, for Brill only (supabase/0029). Her ask, 2026-09-24:
            a lead magnet's landing page came back selling her flagship, because
            25 words could not say what the Assessment is — and the Description
            cannot grow, since it is what GHL shows as the product description.
            Every panel that links this offer reads this box (utils/offerLine). */}
        <Field label={<>Tell Brill more <span style={{ color: B.ghost, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></>}>
          <textarea value={draft.details || ""} onChange={(e) => set("details", e.target.value)} onBlur={commit}
            placeholder="What it is, who it's for, what they get, how it's different from your other offers. As long as it needs to be."
            style={{ ...input, minHeight: 140, resize: "vertical", lineHeight: 1.5 }} />
          <Hint>Brill reads this wherever the offer is linked: sites, courses, scripts, decks, sequences, newsletters. It never goes to your CRM.</Hint>
        </Field>
        <div style={{ borderTop: `1px solid ${B.border}`, marginBottom: 18 }} />
        {/* minmax(0, …), NOT `1fr`, and it is not a nicety. A bare `1fr` track
            will not shrink below its CONTENT's minimum, and the content here
            includes a destination address — "info.tevrawood.com/business-
            blueprint/landing" — which is one unbreakable word. So the left
            column demanded 416px inside a 305px card and shoved the right one
            (Payment plan, Status) clean out of the card, with the page
            scrolling sideways to reach it. Her report, 2026-09-22: "offers
            pushed payment plan over".

            It is the LONG ADDRESS that sets it off, which is why it appeared
            on one offer and not the others — nothing about the form changed.
            With the track allowed to reach zero, the address ellipsises the
            way its own styles always said it should. */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: isMobile ? 0 : 30 }}>
          <div>
            {/* Pricing is a small structure, not one string — a Continuation tier
                needs to say "$97 / month" as data, and the payment-plan answer
                needs somewhere to live. */}
            <Field label="Pricing">
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                {[["flat", "Flat"], ["recurring", "Recurring"]].map(([val, lbl]) => (
                  <Choice key={val} on={!isFree && price.mode === val} onClick={() => setPriceMode(val)}>{lbl}</Choice>
                ))}
                {/* Free is the normal case for a Trust Building rung, so it's a
                    control rather than a word she has to know to type. */}
                <Choice on={isFree} onClick={toggleFree}>Free</Choice>
              </div>
              {!isFree && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    value={price.amount}
                    onChange={(e) => setPrice("amount", e.target.value)}
                    onBlur={commit}
                    placeholder={price.mode === "recurring" ? "e.g. $97" : "e.g. $4,000"}
                    style={{ ...input, flex: 1 }}
                  />
                  {price.mode === "recurring" && (
                    <select value={price.interval} onChange={(e) => setPriceNow("interval", e.target.value)} style={{ ...input, width: 130, flexShrink: 0 }}>
                      <option value="week">/ week</option>
                      <option value="month">/ month</option>
                      <option value="year">/ year</option>
                    </select>
                  )}
                </div>
              )}
              {/* WHAT FREE MEANS ONCE IT LEAVES BRILL. Her ask, 2026-09-22:
                  both her dev offers were Free and it only became obvious when
                  a script printed it. "Free" and "no price" are not the same
                  thing to a CRM — a product with no price attached cannot be
                  bought, cannot sit in a funnel as a purchasable item. For a
                  lead magnet that is exactly right, which is why this is a
                  sentence and not a warning.

                  SETTLED the same day, after checking GHL takes an amount of
                  0: Free goes over as a $0 PRODUCT rather than as no price at
                  all, so it can be added to an order form without her having
                  to finish the job by hand. */}
              {isFree && <Hint>No charge — this rung earns trust, not revenue. Goes to your CRM as a $0 product, so it can sit in a funnel.</Hint>}
              {!isFree && price.mode === "recurring" && (
                <Hint>Recurring pricing logic isn't built into the Accelerator yet — WORTHI prices one-time offers only. For now this is your number, not a recommendation.</Hint>
              )}
            </Field>
            {/* WHERE IT SENDS PEOPLE — chosen, not typed. A page and a meeting
                link are references, so renaming the page or editing the link's
                URL changes it here too. */}
            {/* WHAT KIND OF THING IT IS. Her ask, 2026-09-22, and it is here
                rather than behind the cog because it sits with Pricing in her
                head — what you are selling and what it costs are one thought.
                Three words, no explanation: the labels are the whole idea, and
                rule 9 does not want a caption about them. */}
            <Field label="Kind">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {PRODUCT_TYPES.map((t) => (
                  <Choice key={t.id} on={productTypeOf(draft) === t.id}
                    onClick={() => { setDraft((d) => ({ ...d, productType: t.id })); onSave?.({ ...draft, productType: t.id }); }}>
                    {t.label}
                  </Choice>
                ))}
              </div>
              {/* Only while it has never been asked — once she picks, the row
                  says so and the sentence has nothing left to tell her. */}
              {!draft?.productType && <Hint>Service unless you say otherwise. It is what your CRM is told when this offer is pushed across.</Hint>}
            </Field>

            <Field label={<>Where it sends people <span style={{ color: B.ghost, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></>}>
              <DestinationField destination={destination} pages={pages} sites={sites} ctas={ctas}
                pageState={pageState} disabled={!canSave}
                onPick={pointOffer}
                onMakeLandingPage={() => onMakeLandingPage(draft)}
                onNewMeetingLink={(fields) => onNewMeetingLink(draft, fields)} />
              {destination?.broken ? (
                <Hint><span style={{ color: B.amber }}>
                  {destination.kind === "cta" && destination.detail
                    ? <>This meeting link has no URL yet — add one in <b style={nb}>Run → Meeting Links</b> and it works everywhere at once.</>
                    : <>What this pointed at has been deleted. Choose somewhere else, or clear it.</>}
                </span></Hint>
              ) : destination?.kind === "cta" ? (
                <Hint>Button reads <b style={nb}>{destination.buttonText || destination.label}</b>. Edit it once in Run → Meeting Links and it changes everywhere it's used.</Hint>
              ) : destination?.kind === "page" ? (
                destination.detail.startsWith("/")
                  ? <Hint>Give <b style={nb}>{destination.site || "the site"}</b> a domain behind its cog in Sites and this becomes the full address.</Hint>
                  : null
              ) : null}
            </Field>
          </div>
          <div>
            {/* Tri-state on purpose: nothing selected means she hasn't answered,
                which is not the same as "no". A pull can't answer it for her. */}
            <Field label="Payment plan">
              <div style={{ display: "flex", gap: 8 }}>
                <Choice on={price.paymentPlan === true} onClick={() => setPriceNow("paymentPlan", price.paymentPlan === true ? null : true)}>Yes</Choice>
                <Choice on={price.paymentPlan === false} onClick={() => setPriceNow("paymentPlan", price.paymentPlan === false ? null : false)}>No</Choice>
              </div>
              <Hint>{price.paymentPlan === true ? "Split into instalments." : price.paymentPlan === false ? "Paid in full at purchase." : "Not set yet."}</Hint>
            </Field>
            <Field label="Status">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[["draft", "Draft"], ["locked", "Locked"], ["retired", "Retired"]].map(([val, lbl]) => (
                  <Choice key={val} on={draft.status === val} onClick={() => setNow("status", val)}>{lbl}</Choice>
                ))}
              </div>
              <Hint>
                {draft.status === "locked" ? "Locked offers can be reused as context elsewhere."
                  : draft.status === "retired" ? "Kept on record but no longer selling — moves to its own group at the bottom of the ladder."
                  : "Drafts stay private to you."}
              </Hint>
            </Field>
          </div>
        </div>
      </div>
      </>
      ) : (
      <>
        {/* Brainstorm: full width, and it says out loud what it can see —
            folded to "Reading n of 4", headless and the drafting screen's
            height, like every other chat in Momentum since 2026-09-24. */}
        <ChatPane
          title="Brainstorm with Brill"
          headless foldContext
          height={phone ? CHAT_HEIGHT_PHONE : CHAT_HEIGHT_DRAFTING}
          msgs={msgs} sending={sending} chatText={chatText} setChatText={setChatText} onSend={send}
          contextItems={contextItems}
          /* The opening set differs by flow: a pulled row is being refined, a new
             one is being built from scratch. */
          suggestions={msgs.length === 1
            ? (fromAccelerator
                ? ["Tighten the language", "Is this priced right?"]
                : ["Suggest something based on what I've locked", "I already have an idea"])
            : ["What would make this feel worth paying for later?", "Where does this sit against my other tiers?", "Give me three angles"]}
          /* No "Use this in the form" footer any more. It only ever switched to
             the form — Brill fills the fields itself when it lands on something
             — which is what "← Back to the offer" in the row above now does. Two
             controls for one move, and a line of the chat's height spent on it. */
        />
      </>
      )}
      {mode === "form" && !draft.isNew && (
        <SupportedBy groups={supporters} onOpen={onOpenSupporter} />
      )}
      {/* Everything the pull read, kept verbatim so nothing is lost in the
          extraction — the fields above are a best-effort read of this. */}
      {mode === "form" && fromAccelerator && draft.sourceText && (
        <div style={{ marginTop: 18, border: `1px solid ${B.border}`, borderRadius: 10, background: B.surface, overflow: "hidden" }}>
          <button onClick={() => setShowSource((s) => !s)} style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "12px 16px", color: B.mid, fontSize: 12.5, fontWeight: 600 }}>
            {showSource ? "Hide" : "Show"} what was pulled from My Offer
          </button>
          {showSource && (
            <div style={{ padding: "0 16px 16px", fontSize: 12.5, lineHeight: 1.7, color: B.mid, whiteSpace: "pre-wrap", borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
              {draft.sourceText}
            </div>
          )}
        </div>
      )}
      {/* ── Nothing to press ────────────────────────────────────────────────
          Save offer, Cancel, and a caption saying "Nothing saves until you save
          it" — true when this was written and the last place in Momentum where
          it was. The fields save on the way out now, so this row REPORTS that
          rather than offering a button that promises it, and goes amber and
          names the reason when a save is blocked, because "Saved" and silence
          look identical.

          Cancel goes with Save: there is no pending edit left for it to throw
          away. The way back is in the bar. */}
      {mode === "form" && (
        <RecordFooter backLabel="All offers" onBack={onBack} saving={saving}
          /* A failed write says so HERE, where she is. The banner used to live
             only on the table, so a save that failed on this screen still read
             "Saved" — the exact silence rule 1 warns about. */
          problem={error ? "That didn't save — see the note above" : canSave ? "" : "Give it a name to save it"}
          onDelete={draft.isNew ? null : onDelete} deleteLabel="Delete offer">
          {/* IN THE FOOTER, BESIDE DELETE, because a push is an action on the
              record rather than a field of it — the same slot every other
              record action uses. It reads "Push to GoHighLevel" where there is
              room and "Push" on a phone, with the destination named in the
              dialog she confirms (her note: "push to GoHighLevel is a long
              name"). */}
          <PushToCrm offer={draft} />
        </RecordFooter>
      )}
      {/* Form only. Under a chat that now reaches the bottom of the window it
          would sit below the fold, and the drafting screens carry no caption
          about behaviour she is watching (rule 9). */}
      {mode === "form" && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 18, border: `1px solid ${B.border}`, background: B.surface2, borderRadius: 10, padding: "13px 16px" }}>
          <span style={{ color: B.faint, flexShrink: 0, fontSize: 13, lineHeight: 1.6 }}>ⓘ</span>
          <span style={{ fontSize: 12.5, color: B.mid, lineHeight: 1.6 }}>
            No chat panel on the form on purpose. Brill assists inside the fields you're filling — switch to <b style={nb}>Brainstorm</b> when you want to think out loud against your locked content.
          </span>
        </div>
      )}
    </>
  );
}


/* ── Where it sends people ────────────────────────────────────────────────────
   The field the offer artboard draws (design-work/LinkPicker.dc.html): one
   box, and behind it three ways to fill it — a page from Sites, a meeting link
   from Run, or a pasted URL. It replaced a select that could only name a
   meeting link and, when she had none, told her to leave the offer and go and
   make one (her report, 2026-09-15). Both ways of MAKING a destination now live
   inside it, first in their lists, so she never leaves the offer she is writing:

     Make a landing page for this offer   a landing site for this offer (made
                                          once, then reused) with its Landing
                                          Page, and the offer pointed at it
     New meeting link                     button text and a URL, saved to Run
                                          and chosen in the same move

   Every choice saves as it is made (rule 1) — there is no way out of a menu to
   wait for. Choosing one clears the other two: an offer sends people to one
   place. utils/offerDestination.js holds that rule.

   Nothing in here writes a page or a link itself. It asks: `onMakeLandingPage`
   and `onNewMeetingLink` are the hub's, because making a site belongs to Sites
   and making a link belongs to Run. */
export function DestinationField({
  destination, pages = [], sites = [], ctas = [], pageState, disabled,
  onPick, onMakeLandingPage, onNewMeetingLink,
}) {
  /* IT FLOATS OFF THE FIELD RATHER THAN DROPPING OUT OF IT (ui.jsx). Her
     report, 2026-09-22: on the offer form this field sits low, and the panel
     went off the bottom of the screen with the sites she was choosing between
     below the fold. */
  const { ref: fieldRef, anchor, open, toggle, close: shut } = useAnchoredPanel();
  // Which of the three lists is showing. Pages first, as drawn.
  const [tab, setTab] = useState(destination?.kind || "page");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [newLink, setNewLink] = useState(null);   // { buttonText, link } while she is making one
  const [url, setUrl] = useState(destination?.kind === "url" ? destination.label : "");

  /* SITE FIRST, THEN ITS PAGE. Her call, 2026-09-16: a flat list of pages was
     confusing, because every main site has a Services Page and every landing
     site has a Thank-you Page. So the list is her sites, and a site opens onto
     its own pages. `openSite` is which one is open; it starts on the site of
     the page the offer already points at. A page with no site belongs to the
     first site — the same rule the Sites screen follows. */
  const siteOf = (p) => p.siteId || sites[0]?.id || null;
  const [openSite, setOpenSite] = useState(() => {
    const cur = destination?.kind === "page" ? pages.find((p) => p.id === destination.id) : null;
    return cur ? siteOf(cur) : null;
  });
  const pagesIn = (siteId) => pages.filter((p) => siteOf(p) === siteId);
  const close = () => { shut(); setNewLink(null); setProblem(""); };
  const shownSite = (sites || []).find((x) => x.id === openSite) || null;
  const pick = (patch) => { onPick(patch); close(); };

  /* The two that make something. Each reports back rather than closing on a
     failure, because a menu that shuts on an error looks exactly like one that
     worked. */
  const run = async (fn) => {
    setBusy(true); setProblem("");
    try {
      const ok = await fn();
      if (ok) close();
      else setProblem("That didn't save — try again once you're back online.");
    } finally { setBusy(false); }
  };

  const icon = { page: Home, cta: CalendarDays, url: Link2 };
  const Icon = destination ? icon[destination.kind] : null;

  const optionRow = (id, glyph, title, hint) => {
    const on = tab === id;
    return (
      <div key={id} role="tab" aria-selected={on} onClick={() => { setTab(id); setNewLink(null); setProblem(""); }}
        style={{ display: "flex", gap: 11, padding: "10px 12px", borderRadius: 9, cursor: "pointer", alignItems: "flex-start", background: on ? "var(--bg-quiet)" : "transparent" }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: B.tealQuiet, border: `1px solid ${B.tealLine}`, color: B.teal, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {glyph}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: B.text }}>{title}</div>
          <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.5, marginTop: 3 }}>{hint}</div>
        </div>
      </div>
    );
  };

  const listHead = (text) => (
    <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: B.label, fontWeight: 700, padding: "4px 7px 7px" }}>{text}</div>
  );
  const makeRow = (text, onClick) => (
    <div role="button" onClick={busy ? undefined : onClick}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 7px", borderRadius: 8, cursor: busy ? "wait" : "pointer" }}>
      <Plus size={14} color="var(--accent)" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: B.teal }}>{busy ? "Making it…" : text}</span>
    </div>
  );
  const choiceRow = (key, on, dot, text, meta, onClick) => (
    <div key={key} role="option" aria-selected={on} onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 7px", borderRadius: 8, cursor: "pointer", background: on ? B.tealQuiet : "transparent" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0, margin: "0 4px" }} />
      <span style={{ fontSize: 13, color: on ? B.teal : B.text, fontWeight: on ? 700 : 400, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 11.5, color: B.dim, flexShrink: 0, maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</span>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      <div ref={fieldRef} role="button" aria-haspopup="dialog" aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setTab(destination?.kind || "page");
          const cur = destination?.kind === "page" ? pages.find((p) => p.id === destination.id) : null;
          setOpenSite(cur ? siteOf(cur) : null);
          toggle();
        }}
        style={{
          ...input, display: "flex", alignItems: "center", gap: 11, cursor: disabled ? "not-allowed" : "pointer",
          borderColor: open ? B.tealLine : destination?.broken ? B.amberLine : B.border2,
          boxShadow: open ? "0 0 0 3px var(--accent-quiet)" : "none",
        }}>
        {destination ? (
          <>
            {Icon && <Icon size={14} color={destination.broken ? "var(--warn-text)" : "var(--accent)"} style={{ flexShrink: 0 }} />}
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: destination.broken ? B.amber : B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{destination.label}</span>
            {destination.detail && (
              <span style={{ fontSize: 12, color: destination.broken ? B.amber : B.dim, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", flexShrink: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{destination.detail}</span>
            )}
            <span style={{ flexShrink: 0, padding: "4px 11px", borderRadius: 16, fontSize: 12, border: `1px solid ${B.tealLine}`, background: B.tealQuiet, color: B.teal }}>Change</span>
          </>
        ) : (
          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: B.dim }}>
            {disabled ? "Name the offer first, then choose where it sends people" : "A page, a meeting link, or paste a URL"}
          </span>
        )}
      </div>

      {open && (
        <>
          <div onClick={close} style={{ position: "fixed", inset: 0, zIndex: 119 }} />
          <div role="dialog" aria-label="Where it sends people"
            style={{
              /* Three option rows, a list and its heading: about 520px with
                 nothing in its way. Below the field if that fits, above it if
                 not, and never taller than the room it has. */
              ...floatingPanel(anchor, { width: 440, wants: 520 }),
              background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 12, padding: 7, boxShadow: "var(--shadow-menu)",
            }}>
            <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: B.label, fontWeight: 700, padding: "8px 10px 6px" }}>Point this at</div>
            <div role="tablist">
              {optionRow("page", <Home size={15} />, "A site", "A page on one of your sites, or a new landing site for this offer.")}
              {optionRow("cta", <CalendarDays size={15} />, "A meeting link", "A booking link from Run.")}
              {optionRow("url", <Link2 size={15} />, "Paste a URL", "Anything hosted elsewhere — a video, a PDF, a checkout.")}
            </div>

            <div style={{ margin: "7px 5px 3px", borderTop: `1px solid ${B.border}`, paddingTop: 9, maxHeight: 280, overflowY: "auto" }}>
              {tab === "page" && (
                <>
                  {shownSite ? (
                    <>
                      <div role="button" onClick={() => setOpenSite(null)}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 7px 8px", fontSize: 12, color: B.teal, cursor: "pointer" }}>
                        <ChevronLeft size={13} /> All sites
                      </div>
                      {listHead(`Pages in ${String(shownSite.name || "").trim() || "this site"}`)}
                      {pagesIn(shownSite.id).length === 0 && (
                        <div style={{ padding: "6px 7px 10px", fontSize: 12.5, color: B.dim }}>No pages in this site yet — add one in Sites.</div>
                      )}
                      {pagesIn(shownSite.id).map((p) => {
                        const st = pageState?.(p);
                        return choiceRow(p.id, destination?.kind === "page" && destination.id === p.id,
                          st === "done" ? B.teal : st === "draft" ? B.amberFill : B.ghost,
                          String(p.title || "").trim() || "Untitled page", `/${p.slug || ""}`,
                          () => pick(pointAt("page", p.id)));
                      })}
                    </>
                  ) : (
                    <>
                      {listHead("Sites")}
                      {/* FIRST, as drawn: pages are never made on their own, so
                          this is the only way one appears for an offer. */}
                      {makeRow("Make a landing page for this offer", () => run(onMakeLandingPage))}
                      {sites.map((x) => {
                        const n = pagesIn(x.id).length;
                        const holds = destination?.kind === "page" && pagesIn(x.id).some((p) => p.id === destination.id);
                        return (
                          <div key={x.id} role="option" aria-selected={holds} onClick={() => setOpenSite(x.id)}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 7px", borderRadius: 8, cursor: "pointer", background: holds ? B.tealQuiet : "transparent" }}>
                            <span style={{ fontSize: 13, color: holds ? B.teal : B.text, fontWeight: holds ? 700 : 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {String(x.name || "").trim() || "Untitled site"}
                            </span>
                            <span style={{ flex: 1 }} />
                            <span style={{ fontSize: 11.5, color: B.dim, flexShrink: 0 }}>
                              {x.kind === "landing" ? "Landing site" : "Main site"} · {n} page{n === 1 ? "" : "s"}
                            </span>
                            <ChevronRight size={13} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {tab === "cta" && (
                <>
                  {listHead("Meeting links")}
                  {newLink ? (
                    /* Two fields, not the whole Meeting Links form: what the
                       button says and where it goes are what make it usable.
                       Its name, description and type wait in Run for whenever
                       she wants them. */
                    <div style={{ padding: "4px 7px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <input autoFocus value={newLink.buttonText} onChange={(e) => setNewLink({ ...newLink, buttonText: e.target.value })}
                        placeholder="Button text — e.g. Book a call" aria-label="Button text" style={{ ...input, fontSize: 13 }} />
                      <input value={newLink.link} onChange={(e) => setNewLink({ ...newLink, link: e.target.value })}
                        placeholder="Booking link — https://calendly.com/you/call" aria-label="Booking link" style={{ ...input, fontSize: 13 }} />
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button disabled={busy || !newLink.buttonText.trim()}
                          onClick={() => run(() => onNewMeetingLink({ buttonText: newLink.buttonText.trim(), link: newLink.link.trim() }))}
                          style={{ ...btnGhost, padding: "7px 14px", fontSize: 12.5, borderColor: B.tealLine, color: B.teal, opacity: newLink.buttonText.trim() ? 1 : 0.5 }}>
                          {busy ? "Adding…" : "Add it and use it"}
                        </button>
                        <button onClick={() => setNewLink(null)} style={{ ...btnGhost, padding: "7px 14px", fontSize: 12.5 }}>Back</button>
                        {!newLink.link.trim() && <span style={{ fontSize: 11.5, color: B.dim }}>The link can come later.</span>}
                      </div>
                    </div>
                  ) : makeRow("New meeting link", () => setNewLink({ buttonText: "", link: "" }))}
                  {!newLink && ctas.map((c) => {
                    const noUrl = ctaStatus(c) === "no_url";
                    return choiceRow(c.id, destination?.kind === "cta" && destination.id === c.id,
                      noUrl ? B.amberFill : B.teal, ctaLabel(c), noUrl ? "No URL yet" : c.link,
                      () => pick(pointAt("cta", c.id)));
                  })}
                </>
              )}

              {tab === "url" && (
                <div style={{ padding: "4px 7px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && url.trim()) pick(pointAt("url", url)); }}
                    placeholder="https://…" aria-label="URL" style={{ ...input, fontSize: 13 }} />
                  <div>
                    <button disabled={!url.trim()} onClick={() => pick(pointAt("url", url))}
                      style={{ ...btnGhost, padding: "7px 14px", fontSize: 12.5, borderColor: B.tealLine, color: B.teal, opacity: url.trim() ? 1 : 0.5 }}>
                      Use this link
                    </button>
                  </div>
                </div>
              )}

              {problem && <div style={{ padding: "6px 7px", fontSize: 12, color: B.salmon }}>{problem}</div>}

              {destination && (
                <div style={{ borderTop: `1px solid ${B.border}`, marginTop: 6, paddingTop: 4 }}>
                  <div role="button" onClick={() => pick(pointNowhere())}
                    style={{ padding: "9px 7px", fontSize: 12.5, color: B.dim, cursor: "pointer", borderRadius: 8 }}>
                    Don't send people anywhere yet
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ── Supported by ─────────────────────────────────────────────────────────────
   Everything built FOR this offer, found rather than typed — the reverse of
   every `offer_id` pointing here (utils/offerDestination.js, supportersOf). It
   replaced the "Tied to" text field, her call 2026-09-16: the field goes, and a
   link shows up when a site, a course or a sequence names this offer.

   A row opens the thing it names, because the next thing she wants after
   "what is built for this" is usually to go and look at it. */
export function SupportedBy({ groups = [], onOpen }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 8 }}>Supported by</div>
      {groups.length === 0 ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: "14px 16px", fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>
          Nothing is built for this offer yet. It shows up here on its own when a site or a course names it behind its cog, or an outreach sequence or a call script names it in its settings.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {groups.map((g) => (
            <div key={g.key} style={{ border: `1px solid ${B.border}`, borderRadius: 10, overflow: "hidden", background: B.surface }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: B.surface2, borderBottom: `1px solid ${B.border}` }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label }}>{g.label}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: B.dim }}>{g.items.length}</span>
              </div>
              {g.items.map((it, i) => (
                <div key={it.id} role="button" onClick={() => onOpen?.(g.key, it.record)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: onOpen ? "pointer" : "default", borderTop: i ? `1px solid ${B.border}` : "none" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: B.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
                  <span style={{ flex: 1 }} />
                  {it.meta && <span style={{ fontSize: 11.5, color: B.dim, flexShrink: 0 }}>{it.meta}</span>}
                  {onOpen && <ChevronRight size={13} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
